import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { Form } from "react-bootstrap";
import { colors, constants } from "utils";
import { trackClickedButton } from "utils/analytics";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { useStandards } from "hooks/useStandards/useStandards";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { getGradeLabel, sortPrerequisites, sortStandardsByCode } from "utils/standards";
import { useQuestionAvailability } from "hooks/useCurriculum/useCurriculum";
import { SearchBar } from "components/shared/SearchBar/SearchBar";
import { QuestionFilterView } from "components/pages/LessonsStandards/QuestionFilterView";
import { EMPTY_QUESTION_FILTER, serializeQuestionFilter, fromQuestionFilter } from "components/pages/LessonsStandards/questionFilters";
import { StandardPicker, AccordionSection, StandardRadioRow } from "./StandardPicker";

// Prerequisite tiers by relationships `level` (mirrors StandardDetailView).
const PREREQ_SECTIONS = [
  { level: 1, tier: "Primary", subtitle: "The adjacent standards that directly support learning the Core Standard." },
  { level: 2, tier: "Secondary", subtitle: "The standards that support learning the primary prerequisite standards." },
  { level: 3, tier: "Tertiary", subtitle: "The standards that support learning the Secondary prerequisite standards." },
];

// Hide standards/clusters/domains that have no usable questions (matches the tree
// visibility rules in ViewChangeStandard / StandardNavigationTree).
const stdVisible = (s) => s.count !== 0 && s.challenge_count !== 0;
const clusterVisible = (c) => (c.standards || []).some(stdVisible);
const domainVisible = (d) => (d.clusters || []).some(clusterVisible);
const gradeKeyOf = (code) => (code ? (code.startsWith("HS") ? "HS" : code.split(".")[0]) : "");

const SearchPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 16px 8px;
`;

const StyledSearchBar = styled(SearchBar)`
  border: 1px solid ${colors.blue300};
  flex: none;
  width: 100%;
`;

const GradeField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 220px;
`;

const GradeLabel = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const BrowseHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrowseLabel = styled.span`
  color: #f7622b;
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 18px;
  text-transform: uppercase;
`;

const BrowseName = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
`;

const CountPill = styled.span`
  align-self: center;
  background-color: ${colors.nightlyWoods500};
  border-radius: 9999px;
  color: white;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  padding: 2px 10px;
  white-space: nowrap;
`;

const EmptyResults = styled.p`
  color: ${colors.milkGlass900};
  font-size: 14px;
  font-style: italic;
  line-height: 20px;
  margin: 8px 0;
`;

// The standard-flow per-group edit PUT replaces this one code's assignment. Mirrors
// the lesson flow's buildAssignmentDelta: `questionFilter` (snake_case, optional)
// carries the teacher's Advanced Selection onto the code; omitted when there's no
// active filter so an unfiltered edit keeps the frictionless payload.
export const buildStandardAssignmentUpdate = (groupCode, selected, questionFilter) => ({
  group_code: groupCode,
  track_type: selected.trackType,
  standard_code: selected.code,
  ...(questionFilter ? { question_filter: questionFilter } : {}),
});

export const EditGroupFocus = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const classroomId = params.get("classroomId");
  const assignmentId = params.get("assignmentId");
  const groupCode = params.get("groupCode");
  const classId = params.get("classId");

  const [bundle, setBundle] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [relationships, setRelationships] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [grade, setGrade] = useState("");

  // Advanced Selection (question filter), per selectable option, mirroring
  // LessonEditGroupFocus. Only the selected option's filter is saved; keeping
  // per-option state lets a teacher switch options without losing an in-progress
  // filter. `expandedKey` tracks which option's inline questions+filter is open.
  const [filters, setFilters] = useState({});
  const [expandedKey, setExpandedKey] = useState(null);
  const optionFilterKey = (opt) => `${opt.trackType}|${opt.code || ""}`;
  const getOptionFilter = (opt) => filters[optionFilterKey(opt)] || EMPTY_QUESTION_FILTER;
  const setOptionFilter = (opt, next) => setFilters((prev) => ({ ...prev, [optionFilterKey(opt)]: next }));
  useEffect(() => { setFilters({}); setExpandedKey(null); }, [classroomId, assignmentId, groupCode]);

  const standardsData = useStandards(true);
  const {
    query, results, isSearching, showResults,
    handleInputChange, handleSubmit, inputRef,
  } = useSemanticStandardSearch();

  const goBackToGroups = useCallback(() => {
    const path = constants.EDUCATOR_PATH;
    history.push(classId ? `${path}?classId=${classId}` : path);
  }, [history, classId]);

  // Load the bundle (source of truth) + the new standard's relationships.
  useEffect(() => {
    if (!classroomId) {
      setIsLoading(false);
      setError("Missing classroom id.");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const wrapper = await request(`groups/classroom/${classroomId}/assignment`, { method: "GET" });
        if (cancelled) return;
        const matched = assignmentId
          ? (wrapper?.assignments || []).find((b) => b.assignment_id === assignmentId)
          : (wrapper?.assignments || [])[0];
        setBundle(matched || null);
        setCurrentCard((matched?.assignments || []).find((a) => a.group_code === groupCode) || null);

        const primaryCode = matched?.primary_standard_code;
        if (primaryCode) {
          const rel = await request(`standards/${primaryCode}/relationships`, { method: "GET" });
          if (!cancelled) setRelationships(rel);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.detail || e?.message || "Failed to load standard options.");
          logError("EditGroupFocus: failed to load", e, {
            category: LogCategory.ERROR,
            classroomId,
            assignmentId,
            groupCode,
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classroomId, assignmentId, groupCode]);

  const primaryCode = bundle?.primary_standard_code;
  const primaryName = bundle?.primary_standard_name;

  // Build the selectable options, tagging each with its track at build time.
  const coreOption = useMemo(() => {
    if (!primaryCode) return null;
    const fromTarget = (relationships?.target || []).find((s) => s.code === primaryCode);
    return {
      code: primaryCode,
      name: fromTarget?.name || primaryName || primaryCode,
      description: fromTarget?.description || primaryName || "",
      trackType: "core",
    };
  }, [primaryCode, primaryName, relationships]);

  const prereqTiers = useMemo(() =>
    PREREQ_SECTIONS.map(({ level, tier, subtitle }) => {
      const grp = (relationships?.prerequisites || []).find((g) => g.level === level);
      const standards = sortPrerequisites(grp?.standards).map((s) => ({ ...s, trackType: "prerequisites" }));
      return { level, tier, subtitle, standards };
    }).filter((t) => t.standards.length > 0),
  [relationships]);

  const extensionOptions = useMemo(() => {
    const level1 = (relationships?.extensions || []).find((g) => g.level === 1);
    return sortStandardsByCode(level1?.standards).map((s) => ({ ...s, trackType: "extensions" }));
  }, [relationships]);

  // A standard with no questions can't back a group code, so its row is disabled.
  // Availability comes from the same question call the rows themselves make.
  const availabilityCodes = useMemo(
    () => [...new Set([
      ...(coreOption?.code ? [coreOption.code] : []),
      ...prereqTiers.flatMap((t) => t.standards.map((s) => s.code)),
      ...extensionOptions.map((s) => s.code),
    ].filter(Boolean))],
    [coreOption, prereqTiers, extensionOptions],
  );
  const { availability, resolved: availabilityResolved } = useQuestionAvailability({
    flow: "standard",
    codes: availabilityCodes,
  });

  // ---- Search / Browse Standards Library (assigns via the "other" track) ----
  const availableGrades = useMemo(
    () => (standardsData || []).filter((g) => (g.domains || []).some(domainVisible)).map((g) => g.grade),
    [standardsData],
  );
  const primaryGrade = gradeKeyOf(primaryCode);

  // Default the grade filter to the Core standard's grade. Wait for primaryGrade
  // (from the bundle fetch) as well as the standards list — otherwise, if
  // standards load first, we'd default to availableGrades[0] and the `if (grade)`
  // guard would then block the correction once the Core standard arrives.
  useEffect(() => {
    if (grade || availableGrades.length === 0 || !primaryGrade) return;
    setGrade(availableGrades.includes(primaryGrade) ? primaryGrade : availableGrades[0]);
  }, [grade, availableGrades, primaryGrade]);

  const gradeDomains = useMemo(() => {
    const g = (standardsData || []).find((x) => x.grade === grade);
    return (g?.domains || []).filter(domainVisible);
  }, [standardsData, grade]);

  // Only standards (not domains/clusters) are assignable from search results.
  const searchStandards = useMemo(
    () => (results || []).filter((r) => r.entity_type === "standard"),
    [results],
  );

  // Pre-select the option matching the code's current standard, and hydrate its
  // stored Advanced Selection filter so it shows on load (and survives re-save).
  useEffect(() => {
    if (selected || !currentCard?.standard_code) return;
    const all = [
      ...(coreOption ? [coreOption] : []),
      ...prereqTiers.flatMap((t) => t.standards),
      ...extensionOptions,
    ];
    let option = all.find((o) => o.code === currentCard.standard_code) || null;
    if (!option && currentCard.track_type === "other") {
      option = { code: currentCard.standard_code, name: currentCard.standard_name, description: "", trackType: "other" };
    }
    if (!option) return;
    setSelected(option);
    const hydrated = fromQuestionFilter(currentCard.question_filter);
    if (hydrated) setFilters((prev) => ({ ...prev, [optionFilterKey(option)]: hydrated }));
  }, [coreOption, prereqTiers, extensionOptions, currentCard, selected]);

  // Enable Save when the standard changed OR the filter differs from what's stored.
  // A standard change is compared by code; the filter is compared against the
  // hydrated stored value so merely re-opening the panel isn't treated as a change.
  const standardChanged = Boolean(selected && selected.code !== currentCard?.standard_code);
  const selectedQuestionFilter = selected ? serializeQuestionFilter(getOptionFilter(selected)) : null;
  const storedQuestionFilter = serializeQuestionFilter(fromQuestionFilter(currentCard?.question_filter) || EMPTY_QUESTION_FILTER);
  const filterChanged = !standardChanged && JSON.stringify(selectedQuestionFilter) !== JSON.stringify(storedQuestionFilter);
  const hasChanged = Boolean(standardChanged || filterChanged);

  const onAssign = async () => {
    if (!hasChanged || saving || !classroomId || !bundle?.assignment_id || !groupCode) return;
    setSaving(true);
    setError(null);
    try {
      await request(`groups/classrooms/${classroomId}/assignments/${bundle.assignment_id}`, {
        method: "PUT",
        payload: { assignments: [buildStandardAssignmentUpdate(groupCode, selected, selectedQuestionFilter)] },
      });
      trackClickedButton("Assign Group Focus", {
        page_name: "Edit Group Focus",
        assignment_type: "Standard",
        class: classroomId || null,
        assignment_id: bundle.assignment_id,
        group_code: groupCode,
        standard_code: selected.code,
        track_type: selected.trackType,
      });
      goBackToGroups();
    } catch (e) {
      setError(e?.detail || e?.message || "Failed to assign standard.");
      logError("EditGroupFocus: failed to assign", e, {
        category: LogCategory.ERROR,
        classroomId,
        assignmentId: bundle?.assignment_id,
        groupCode,
        standardCode: selected?.code,
      });
    } finally {
      setSaving(false);
    }
  };

  // Every row (core / prereq / extension / library) opens the Advanced Selection
  // filter + filtered question carousel inline — standard-scoped to that row's code,
  // no modal. Auto-assign-early-finishers is a per-group setting that applies to
  // whatever the group is assigned to, so it's offered on every track, not just core.
  // Open the section holding the group's CURRENT focus on load (core falls back
  // as the default for unassigned codes) — sections mount only after the bundle
  // has loaded (StandardPicker gates children on isLoading), so the track is
  // always known when defaultExpanded is captured.
  const currentTrack = currentCard?.track_type || "core";

  const renderRow = (option, pl) => {
    const rowKey = `${option.trackType}:${option.code}`;
    // Only once probed — an unresolved code stays selectable rather than flickering
    // disabled. Library-search rows aren't probed and are already question-filtered.
    const noQuestions = availabilityResolved && availability[option.code] === false;
    return (
      <StandardRadioRow
        key={rowKey}
        option={option}
        pl={pl}
        disabled={noQuestions}
        checked={selected?.code === option.code && selected?.trackType === option.trackType}
        onSelect={setSelected}
        expanded={expandedKey === rowKey}
        onToggleView={() => setExpandedKey((k) => (k === rowKey ? null : rowKey))}
        expandedContent={(
          <QuestionFilterView
            standardCode={option.code}
            filter={getOptionFilter(option)}
            onFilterChange={(next) => setOptionFilter(option, next)}
            showStandards={false}
            showAutoAssign
            background={colors.milkGlass200}
          />
        )}
      />
    );
  };

  return (
    <StandardPicker
      groupCode={groupCode}
      currentCard={currentCard}
      isLoading={isLoading}
      saving={saving}
      canAssign={Boolean(hasChanged)}
      onAssign={onAssign}
      onCancel={goBackToGroups}
      error={error}
      onClearError={() => setError(null)}
    >
      {coreOption && (
        <AccordionSection title={`Core: ${coreOption.code} ${coreOption.name}`} defaultExpanded={currentTrack === "core"}>
          {renderRow(coreOption, 36)}
        </AccordionSection>
      )}

      {prereqTiers.length > 0 && (
        <AccordionSection title={`Prerequisites to ${primaryCode}`} defaultExpanded={currentTrack === "prerequisites"}>
          {prereqTiers.map((t) => (
            <AccordionSection key={t.level} level={1} title={`${t.tier} Prerequisites for ${primaryCode}`} defaultExpanded>
              {t.standards.map((o) => renderRow(o, 60))}
            </AccordionSection>
          ))}
        </AccordionSection>
      )}

      {extensionOptions.length > 0 && (
        <AccordionSection title={`Extensions to ${primaryCode}`} defaultExpanded={currentTrack === "extensions"}>
          {extensionOptions.map((o) => renderRow(o, 36))}
        </AccordionSection>
      )}

      <AccordionSection title="Search or Browse Standards Library" defaultExpanded={currentTrack === "other"}>
        <SearchPanel>
          <StyledSearchBar
            ref={inputRef}
            value={query}
            onChange={handleInputChange}
            onSubmit={handleSubmit}
            placeholder={'Try: "Multiplying Fractions" or "Proportional relationships"'}
            ariaLabel="Search or browse standards library"
            isLoading={isSearching}
          />
          {!showResults && (
            <GradeField>
              <GradeLabel as="label" htmlFor="egf-grade-select">Grade</GradeLabel>
              <Form.Select id="egf-grade-select" value={grade} onChange={(e) => setGrade(e.target.value)}>
                {availableGrades.map((g) => (
                  <option key={g} value={g}>{getGradeLabel(g)}</option>
                ))}
              </Form.Select>
            </GradeField>
          )}
        </SearchPanel>

        {showResults ? (
          <>
            <span role="status" aria-live="polite" style={{ position: "absolute", width: 1, height: 1, margin: -1, padding: 0, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}>
              {searchStandards.length > 0
                ? `${searchStandards.length} standard${searchStandards.length === 1 ? "" : "s"} found for ${query}`
                : `No standards found for ${query}`}
            </span>
            {searchStandards.length > 0
              ? searchStandards.map((s) =>
                renderRow({ code: s.code, name: s.name, description: s.description, trackType: "other" }, 16))
              : <EmptyResults>No standards found for &ldquo;{query}&rdquo;.</EmptyResults>}
          </>
        ) : (
          gradeDomains.map((domain) => {
            const count = (domain.clusters || []).reduce(
              (n, c) => n + (c.standards || []).filter(stdVisible).length, 0);
            return (
              <AccordionSection
                key={domain.code}
                level={1}
                header={(
                  <BrowseHeader>
                    <BrowseLabel>Domain - {domain.code}</BrowseLabel>
                    <BrowseName>{domain.name}</BrowseName>
                  </BrowseHeader>
                )}
                right={<CountPill>{count} {count === 1 ? "Standard" : "Standards"}</CountPill>}
              >
                {(domain.clusters || []).filter(clusterVisible).map((cluster) => (
                  <AccordionSection
                    key={cluster.code}
                    level={2}
                    header={(
                      <BrowseHeader>
                        <BrowseLabel>Cluster - {cluster.code}</BrowseLabel>
                        <BrowseName>{cluster.name}</BrowseName>
                      </BrowseHeader>
                    )}
                  >
                    {(cluster.standards || []).filter(stdVisible).map((s) =>
                      renderRow({ code: s.code, name: s.name, description: s.description, trackType: "other" }, 84))}
                  </AccordionSection>
                ))}
              </AccordionSection>
            );
          })
        )}
      </AccordionSection>
    </StandardPicker>
  );
};
